import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuthContext } from './AuthContext';

interface TimezoneContextType {
  userTimezone: string;
  setUserTimezone: (timezone: string) => Promise<void>;
  loading: boolean;
}

const TimezoneContext = createContext<TimezoneContextType | undefined>(undefined);

export function TimezoneProvider({ children }: { children: React.ReactNode }) {
  // Get auth context - this should work since TimezoneProvider is nested inside AuthProvider
  const authContext = useAuthContext();
  const user = authContext?.user;
  const [userTimezone, setUserTimezoneState] = useState<string>('UTC');
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    if (user) {
      fetchUserTimezone();
    } else {
      // Se não houver usuário logado, usar o timezone do navegador como fallback
      const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      setUserTimezoneState(browserTimezone);
      setLoading(false);
    }
  }, [user]);

  const fetchUserTimezone = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('users')
        .select('timezone')
        .eq('id', user?.id)
        .single();

      if (error) throw error;

      if (data && data.timezone) {
        setUserTimezoneState(data.timezone);
      } else {
        // Se o usuário não tiver um timezone configurado, usar o timezone do navegador como fallback
        const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        setUserTimezoneState(browserTimezone);
        
        // Salvar o timezone do navegador como padrão para o usuário
        await supabase
          .from('users')
          .update({ timezone: browserTimezone })
          .eq('id', user?.id);
      }
    } catch (error) {
      console.error('Erro ao carregar o timezone do usuário:', error);
      // Fallback para o timezone do navegador em caso de erro
      const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      setUserTimezoneState(browserTimezone);
    } finally {
      setLoading(false);
    }
  };

  const setUserTimezone = async (timezone: string) => {
    if (!user) return;
    
    try {
      setLoading(true);
      
      // Atualizar no banco de dados
      const { error } = await supabase
        .from('users')
        .update({ timezone })
        .eq('id', user.id);

      if (error) throw error;
      
      // Atualizar o estado local
      setUserTimezoneState(timezone);
    } catch (error) {
      console.error('Erro ao atualizar o timezone do usuário:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  return (
    <TimezoneContext.Provider
      value={{
        userTimezone,
        setUserTimezone,
        loading
      }}
    >
      {children}
    </TimezoneContext.Provider>
  );
}

export function useTimezone() {
  const context = useContext(TimezoneContext);
  if (context === undefined) {
    throw new Error('useTimezone must be used within a TimezoneProvider');
  }
  return context;
} 